import {
  BACKEND_UNAVAILABLE_RELOAD_COOLDOWN,
  BACKEND_UNAVAILABLE_RELOAD_GUARD,
} from '@/shared/config';
import { randomInt } from '@/shared/lib/utils/random-int';
import {
  Alert,
  Button,
  Center,
  Group,
  Loader,
  Progress,
  Stack,
  Text,
} from '@mantine/core';
import { useReducedMotion } from '@mantine/hooks';
import IconAlertTriangle from '@tabler/icons-react/dist/esm/icons/IconAlertTriangle.mjs';
import IconCircleCheck from '@tabler/icons-react/dist/esm/icons/IconCircleCheck.mjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiHealth } from './use-api-health';

interface BackendUnavailableProps {
  initialError?: unknown;
}

interface ReloadSchedule {
  secondsLeft: number;
  totalSeconds: number;
}

export const BackendUnavailable = ({
  initialError,
}: BackendUnavailableProps) => {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();
  const { attempt, isReady } = useApiHealth(initialError);
  const [schedule, setSchedule] = useState<ReloadSchedule | null>(null);
  const [requiresManualReload, setRequiresManualReload] = useState(false);
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined,
  );

  const clearReloadTimers = useCallback(() => {
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    reloadTimerRef.current = undefined;
    countdownTimerRef.current = undefined;
  }, []);

  const reloadNow = useCallback(() => {
    clearReloadTimers();
    sessionStorage.setItem(
      BACKEND_UNAVAILABLE_RELOAD_GUARD,
      String(Date.now()),
    );
    window.location.reload();
  }, [clearReloadTimers]);

  useEffect(() => {
    clearReloadTimers();
    setSchedule(null);
    setRequiresManualReload(false);
    if (!isReady) return;

    const lastReload = Number(
      sessionStorage.getItem(BACKEND_UNAVAILABLE_RELOAD_GUARD),
    );
    if (
      Number.isFinite(lastReload) &&
      Date.now() - lastReload < BACKEND_UNAVAILABLE_RELOAD_COOLDOWN
    ) {
      setRequiresManualReload(true);
      return;
    }

    const totalSeconds = randomInt(2, 12);
    const deadline = Date.now() + totalSeconds * 1000;
    const updateCountdown = () => {
      const secondsLeft = Math.max(
        0,
        Math.ceil((deadline - Date.now()) / 1000),
      );
      setSchedule({ secondsLeft, totalSeconds });
    };

    updateCountdown();
    countdownTimerRef.current = setInterval(updateCountdown, 1000);
    reloadTimerRef.current = setTimeout(reloadNow, totalSeconds * 1000);

    return clearReloadTimers;
  }, [clearReloadTimers, isReady, reloadNow]);

  const title = isReady
    ? t('app.backendUnavailable.recovered')
    : t('app.backendUnavailable.title');

  return (
    <Center h='100dvh' data-testid='backend-unavailable'>
      <Alert
        icon={
          isReady ? (
            <IconCircleCheck size={18} />
          ) : (
            <IconAlertTriangle size={18} />
          )
        }
        color={isReady ? 'green' : 'yellow'}
        variant='light'
        title={
          <span role='status' aria-live='polite'>
            {title}
          </span>
        }
        maw={440}
      >
        <Stack gap='sm'>
          <Text size='sm'>
            {isReady
              ? t(
                  requiresManualReload
                    ? 'app.backendUnavailable.recoveredManualDescription'
                    : 'app.backendUnavailable.recoveredDescription',
                )
              : t('app.backendUnavailable.description')}
          </Text>

          {isReady ? (
            <>
              {schedule && (
                <Progress
                  aria-hidden='true'
                  color='green'
                  size='xs'
                  transitionDuration={reducedMotion ? 0 : 1000}
                  value={(schedule.secondsLeft / schedule.totalSeconds) * 100}
                />
              )}
              <Group justify='space-between' gap='sm' wrap='nowrap'>
                <Text size='xs' aria-live='off'>
                  {schedule
                    ? t('app.backendUnavailable.reloadingIn', {
                        count: schedule.secondsLeft,
                      })
                    : t('app.backendUnavailable.reloadManually')}
                </Text>
                <Button variant='default' size='xs' onClick={reloadNow}>
                  {t('app.backendUnavailable.reloadNow')}
                </Button>
              </Group>
            </>
          ) : (
            <Group gap='xs'>
              <Loader size='xs' />
              <Text size='xs'>
                {t('app.backendUnavailable.checking', {
                  count: Math.max(attempt, 1),
                })}
              </Text>
            </Group>
          )}
        </Stack>
      </Alert>
    </Center>
  );
};